'use client';

import * as Sentry from '@sentry/nextjs';
import { PropsWithChildren, useCallback, useEffect } from 'react';

import { LegalEntityConsentModal } from '@/features/legal-entity-consent';
import { UserV1 } from '@/shared/api';

import { AuthContext } from './auth-context';
import { useAuthState } from './use-auth-state';
import { useOnboardingRedirect } from './use-onboarding-redirect';

export function AuthProvider({
	children,
	initialUser,
}: PropsWithChildren<{ initialUser?: UserV1 | null }>) {
	const value = useAuthState(initialUser);
	const { user, refresh, isNeedOnboarding } = value;

	useOnboardingRedirect(isNeedOnboarding);

	const showLegalConsent = !!user && user.acceptedNewLegalEntity !== true;

	const handleAcceptLegalEntity = useCallback(
		async (advertisingMail: boolean) => {
			await fetch('/api/accept-legal-entity', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ advertisingMail }),
			});
			await refresh();
		},
		[refresh],
	);

	useEffect(() => {
		if (!user) {
			return;
		}

		Sentry.setUser({
			id: user.login,
			email: user.email,
			username: user.login,
			role: user.role,
		});
	}, [user]);

	return (
		<AuthContext.Provider value={value}>
			{children}
			{showLegalConsent && (
				<LegalEntityConsentModal onAccept={handleAcceptLegalEntity} />
			)}
		</AuthContext.Provider>
	);
}
